import { X } from 'lucide-react';
import { useCallback, useEffect, useState, type ReactElement, type ReactNode } from 'react';

import { useAuth } from '../auth/AuthProvider.js';
import { useBranding } from '../branding/BrandingProvider.js';

import { JobDrawer } from './JobDrawer.js';
import { Sidebar } from './Sidebar.js';
import { SuspendedTenantBanner } from './SuspendedTenantBanner.js';
import { TopBar } from './TopBar.js';

/**
 * The frame every authenticated screen renders inside (plan F1).
 *
 * The skip link is first in the DOM on purpose: without it a keyboard user tabs
 * through the entire sidebar on every single page before reaching content, and
 * "the app is technically operable by keyboard" stops being a meaningful claim.
 *
 * **Structure: a full-height rail beside a content column.** Phase 04 stacked a
 * bar above a row of (rail + main), which meant the two pieces of chrome met at
 * a corner and the rail restarted below the bar. A rail that runs the full
 * height reads as the app's spine rather than as a panel, and it lets the
 * context bar belong to the *content* column — which is what makes it sensible
 * for the bar and the table header inside `main` to share one scroll container.
 *
 * **Brightness means data.** The chrome — rail, context bar, footer — is
 * `surface-alt` and recedes; only containers that hold real information are the
 * bright `surface` plane. The inverse (a white bar and rail over a grey canvas
 * of white cards) is what made every element rank equally.
 *
 * **The hairline, not the fill, is the structural separator.** A tenant may set
 * `surface` and `surface-alt` to the same value, which collapses the inversion
 * entirely; the `border-line` edge on the rail, bar and footer is what survives
 * that, so the layout never depends on two tenant colours differing.
 */
export function AppShell({ children }: { children: ReactNode }): ReactElement {
  const { tenant } = useAuth();
  const { branding } = useBranding();

  /**
   * Below `md` the rail is not rendered, so without this the navigation is
   * unreachable on a phone. It is a *conditional* mount rather than a hidden
   * second copy: two `<nav aria-label="Main">` elements in the DOM would give a
   * screen reader two identical landmarks, and every "the sidebar shows exactly
   * these links" assertion would match twice.
   */
  const [navOpen, setNavOpen] = useState(false);
  const closeNav = useCallback(() => setNavOpen(false), []);

  return (
    <div className="flex min-h-screen bg-surface-alt">
      {/*
        First in the DOM, always. It is a floating chip rather than a bare word
        so that when it appears it reads as a control the tenant styled, not as
        unstyled text that arrived by accident. The card treatment is spelled
        out in utilities instead of reusing the `.card` composition, because a
        variant (`focus:`) can only be applied to a generated utility — a
        `focus:card` candidate is simply never emitted, and the chip would ship
        transparent.
      */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-[var(--space-4)] focus:top-[var(--space-4)] focus:z-50 focus:rounded-[var(--radius-md)] focus:border focus:border-line-strong focus:bg-surface focus:px-[var(--space-3)] focus:py-[var(--space-2)] focus:text-[length:var(--text-sm)] focus:font-medium focus:text-body focus:shadow-[var(--elevation-3)]"
      >
        Skip to content
      </a>

      {/*
        `sticky h-screen`: the rail is navigation, and navigation that scrolls
        away on a long estate list is navigation you have to scroll back up to
        reach. It stays put and scrolls internally if a tenant ever has more
        items than fit.
      */}
      <aside className="chrome hidden h-screen w-[var(--rail-w)] shrink-0 self-start overflow-y-auto border-r border-line md:sticky md:top-0 md:block">
        <Sidebar />
      </aside>

      {navOpen ? <MobileNav onClose={closeNav} /> : null}

      {/*
        `min-w-0`: without it a wide table inside `main` sets the flex item's
        minimum size to its content and pushes the rail off-screen instead of
        scrolling inside its own container.
      */}
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar onOpenNav={() => setNavOpen(true)} />
        <SuspendedTenantBanner tenant={tenant} />

        {/*
          A measured canvas. Unbounded, a 40px table row and four stat tiles
          stretch across a 2500px monitor, which is the single most "admin
          template" property the old layout had. Nothing renders text directly
          on this ground — every screen's content sits in a card or the page
          header band — so `text-muted` keeps its measured contrast against
          `surface` rather than landing on an unmeasured tinted background.
        */}
        {/*
          The gutter is responsive because 32px of it is not. On a 360px phone
          the old fixed `px-8` spent 18% of the viewport on margin, which is
          the difference between an estate table that scrolls a little and one
          that scrolls constantly. It steps up to the measured desktop gutter at
          `md`, where the rail appears and there is room for it.
        */}
        <main
          id="main"
          className="mx-auto w-full max-w-[var(--content-max)] flex-1 px-[var(--space-4)] py-[var(--space-5)] md:px-[var(--space-8)] md:py-[var(--space-6)]"
        >
          {children}
        </main>

        <footer className="chrome border-t border-line px-[var(--space-4)] py-[var(--space-3)] text-[length:var(--text-2xs)] text-muted md:px-[var(--space-8)]">
          <div className="mx-auto flex w-full max-w-[var(--content-max)] flex-wrap items-center justify-between gap-[var(--space-2)]">
            <span>{branding?.footerText ?? ''}</span>
            {branding?.supportEmail ? (
              <a className="underline" href={`mailto:${branding.supportEmail}`}>
                {branding.supportEmail}
              </a>
            ) : null}
          </div>
        </footer>
      </div>

      <JobDrawer />
    </div>
  );
}

/**
 * The rail, as a drawer, for viewports that do not have room for it.
 *
 * It renders the **same** `<Sidebar>` — permission and feature gating included
 * — because a mobile navigation that maintains its own list of links is a
 * second place for "this tenant has not bought messaging" to be got wrong.
 */
function MobileNav({ onClose }: { onClose: () => void }): ReactElement {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-40 flex md:hidden">
      {/*
        The scrim is decoration and says so. It was a real `<button>`, on the
        argument that dismissal must be reachable by keyboard — which was right
        about the requirement and wrong about which element answers it. A
        focusable element whose accessible name is the same action as a visible
        control beside it is a second tab stop that leads nowhere a reader can
        see, and once it is `aria-hidden` — which a backdrop should be — a
        focusable child of a hidden subtree is an axe violation in its own
        right. Dismissal is now carried by the two things a keyboard user
        actually reaches for: Escape, and the labelled close control below.
      */}
      <div aria-hidden="true" onClick={onClose} className="absolute inset-0 bg-scrim/55" />
      <div className="chrome relative flex h-full w-[var(--rail-w)] max-w-[80vw] flex-col overflow-y-auto border-r border-line shadow-[var(--elevation-3)]">
        {/*
          Sits in the rail's own brand block, which is exactly `--topbar-h`
          tall — so on the viewport where the drawer exists, the close control
          lands at the same height as the menu button that opened it, and the
          drawer reads as that button's panel rather than as a new screen.
        */}
        <button
          type="button"
          aria-label="Close navigation"
          onClick={onClose}
          // The underscores are Tailwind's escape for the spaces `calc()`
          // requires around a `-`: written `(var(--topbar-h)-2rem)` the
          // expression is invalid CSS and the declaration is dropped in
          // silence, which is the same class of failure this codebase records
          // for interpolated class names.
          className="absolute right-[var(--space-2)] top-[calc((var(--topbar-h)_-_2rem)_/_2)] z-10 inline-flex size-8 items-center justify-center rounded-[var(--radius-sm)] text-muted transition-colors duration-[var(--motion-fast)] hover:bg-surface hover:text-body"
        >
          <X aria-hidden="true" size={18} strokeWidth={2} />
        </button>

        {/* `onNavigate` closes the drawer the moment a link is followed — a
            drawer left open covers the screen the user just asked for. */}
        <Sidebar onNavigate={onClose} />
      </div>
    </div>
  );
}
